import { service } from '@ember/service';
import Component from '@glimmer/component';
import { registerDestructor } from '@ember/destroyable';
import StickyLayout from 'tio-ui/components/sticky-layout';
import { Section } from 'tio-ui/components/layout';
import TooltipButton from 'tio-reimagined/components/tooltip-button';
import Drawer from 'tio-reimagined/components/drawer';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import stickySidebarAdjustment from 'tio-ui/modifiers/sticky-sidebar-adjustment';
import { AdvancedProgramFilters } from './programs-filters.gts';
import { DatasetFilters } from '../dataset-filters.gts';
import ProgramsTable from './programs-table.gts';
import ProgramsAggregateTable from './programs-aggregate-table.gts';
import SavedReportsDrawer from '../saved-reports-drawer.gts';
import ChartView from '../chart-view.gts';
import ProgramsDataProvider from './programs-data-provider.gts';
import { tracked } from '@glimmer/tracking';
import { eq, gt } from 'tio-ui/utilities';
import { trackedFunction } from 'reactiveweb/function';
import { on } from '@ember/modifier';
import { fn, hash } from '@ember/helper';
import { PhFunnel, PhFloppyDisk, PhTrayArrowDown } from 'ember-phosphor-icons';
import ReSelect from 'tio-reimagined/components/select';
import { type FieldOption } from '../../utils/opensearch-field-mapper';
import { OpenSearchFieldMapper } from '../../utils/opensearch-field-mapper';
import { ChartState } from '../../utils/chart-state';
import {
  getFieldGroup as sharedGetFieldGroup,
  getDisplayGroup as sharedGetDisplayGroup,
  stripTypeSuffixes,
} from '../../utils/label-generation';
import {
  applyTemplateAndCustomFieldLabels,
  extractEmployeeSourceField,
} from '../../utils/field-label-resolver';
import { normalizeFieldKey, groupFieldOptions } from '../../utils/column-utils';
import ColumnSelector from '../column-selector';
import { deriveFilterColumnDependencies } from '../../utils/filter-registry';
import { applyFallbackEnumValues } from '../../utils/dataset-filter-enums';
import { and } from 'tio-ui/utilities';
import { getDefaultColumns, getModelTypeFromDataset } from '../../utils/dataset-utils';
import { AGGREGATION_COLUMNS, DEFAULT_COLUMNS } from '../../utils/tas-column-config';
import type { NotificationsService } from 'tio-reimagined/components/notifications';

import type CompanyModel from 'tio-common/models/company';
import type ReportModel from '../../models/reports';
import type { SearchConfig } from '../../models/reports';
import type Store from '@ember-data/store';
import type TasProgramTemplateModel from 'tio-common/models/tas-program-template';
import type IntlService from 'ember-intl/services/intl';
import type { ComponentLike } from '@glint/template';

import type {
  SearchAdapter,
  SearchFieldPayload,
  SearchFieldsResponse,
  SearchMappingWithLabelMapsResponse,
} from 'tio-common/models/search';

interface EmployeeCustomFieldMapping {
  fieldName?: string;
  label?: string;
  sourceField?: string;
  reportDatatype?: string;
  enumValues?: string[];
}

interface EmployeeCustomFieldMappingsResponse {
  mappings?: EmployeeCustomFieldMapping[];
}

type SearchAdapterWithEmployeeCustomMappings = SearchAdapter & {
  employeeCustomFieldMappings?: (
    companyId: string,
    programTemplateId?: string
  ) => Promise<EmployeeCustomFieldMappingsResponse>;
};

interface ParsedFieldsResult {
  fields: FieldOption[];
  sourceFieldByFieldKey: Record<string, string>;
  apiLabelByFieldKey: Record<string, string>;
}

const GLOBAL_EMPLOYEE_SOURCE_FIELDS = new Set([
  'id',
  'person_id',
  'first_name',
  'last_name',
  'email',
  'employment_start_date',
  'employment_end_date',
  'payroll_id',
  'phone_number',
  'title',
  'department',
  'cost_center',
  'region',
  'eligibility_start_date',
  'eligibility_end_date',
]);

const INTERNAL_FLAT_OBJECT_LEAF_SEGMENTS = new Set(['s', 'n', 'b', 'd', 'i', 'l', 'f']);
const GENERIC_LEAF_FIELD_NAMES = new Set(['id', 'name']);
const RANGE_QUERY_TYPES = new Set(['date', 'integer', 'long', 'float', 'double', 'short']);

/**
 * Controller interface - apps provide their own controller that matches this signature
 */
export interface TasProgramsControllerInterface {
  // Pagination
  page: number;
  limit: number;

  // Dataset selection
  dataset?: string;

  // Filter properties
  programTemplateId?: string;
  programCreatedAtStart?: string;
  programCreatedAtEnd?: string;
  excludeNonVoucherPrograms?: boolean;
  courseCashProgramsOnly?: boolean;
  showIndividualApplications?: boolean;
  applicationStates?: string;
  applicationCreatedAtStart?: string;
  applicationCreatedAtEnd?: string;
  applicationDateRangePreset?: string;
  includePayments?: boolean;
  paymentStates?: string;
  paymentYear?: string;
  includeCourses?: boolean;
  coursesBeginDate?: string;
  coursesEndDate?: string;
  employeeId?: string;
  terminatedEmployees?: boolean;
  excludeTermVerified?: boolean;
  approverId?: string;
  includeInvoiceDate?: boolean;
  selectedColumns?: string;
  sortField?: string;

  // Method to update properties
  setProperties(updates: Record<string, unknown>): void;
}

export interface ProgramsPageSignature {
  Args: {
    company?: CompanyModel; // Optional for global analytics context
    controller: TasProgramsControllerInterface;

    // Route configuration
    baseRoute: string; // e.g., "authenticated.admin.tuition-assistance" or "authenticated.companies.show.tuition-assistance"
    applicationDetailRoute: string;
    programDetailRoute: string;

    // Optional customizations
    showViewToggle?: boolean; // Whether to show table/chart toggle
    showSaveReports?: boolean; // Whether to show save/load reports functionality
    showColumnSelector?: boolean; // Whether to show the Columns tab in the drawer (default: true)
    dataProviderComponent?: ComponentLike;
    filterColumnDependencies?: Record<string, string[]>; // Custom filter-to-column mappings
    isAdminContext?: boolean; // Whether this is being used in an admin context (shows admin-only fields)
    isGlobalContext?: boolean; // Whether this is global analytics (no company filter)
    initialReportId?: string | number; // Optional report id to auto-load on first render
    onCompanyChange?: (company?: CompanyModel) => void; // Optional callback when company selection changes in drawer
  };
  Blocks: {
    breadcrumbs?: [];
    headerActions?: [
      {
        downloadCsv: () => Promise<unknown>;
        currentView: 'table' | 'chart';
        onViewChange: (view: 'table' | 'chart') => void;
        selectedDataset: string;
        onDatasetChange: (dataset: string) => void;
      },
    ];
  };
}

export default class ProgramsPageComponent extends Component<ProgramsPageSignature> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;

  @tracked currentView: 'table' | 'chart' = 'table';
  @tracked currentReport?: ReportModel;
  @tracked columnOrder: string[] = [];

  /** Chart configuration — extracted to a standalone tracked class. */
  chart = new ChartState();

  /**
   * Memoisation cache for selectedColumnsArray.
   * `trackedFunction` (and Glimmer autotracking in general) triggers downstream
   * re-runs whenever it sees a new object reference, even if the contents are
   * identical.  Because the getter re-derives a new `string[]` on every access
   * (array literals always create fresh references), the data provider perceives
   * a "change" and re-fires the OpenSearch query.
   *
   * By caching the previous result and returning the same reference when the
   * column list hasn't materially changed, we cut one full redundant search
   * round-trip on initial page load.
   */
  private _prevSelectedColumns: string[] = [];
  private _prevNormalizedSelectedColumnsKey = '';
  private _prevNormalizedSelectedColumns = new Set<string>();

  // Sidebar state
  @tracked isExpandedSidebar = true;

  // Drawer state
  @tracked selectedDrawerType: string | null = null;
  @tracked isDrawerOpen = false;
  @tracked activeFilterTab: 'filters' | 'columns' = 'filters';

  // Column removal warning modal
  @tracked showColumnWarningModal = false;
  @tracked columnToRemove: string | null = null;
  @tracked affectedFilters: string[] = [];

  private hasInitializedDynamicScope = false;
  private previousDynamicScopeCompanyId?: string;
  private previousDynamicScopeProgramTemplateId?: string;

  // Responsive media query using tracked property
  @tracked private _isLargeScreen = false;

  // Generic dataset filters (for non-TAS datasets)
  @tracked genericFilters: Record<string, unknown> = {};

  get isTasDataset(): boolean {
    return this.chart.selectedDataset === 'tas-program-instances';
  }

  get effectiveShowIndividualApplications(): boolean {
    return this.isTasDataset ? Boolean(this.args.controller.showIndividualApplications) : true;
  }

  constructor(owner: unknown, args: ProgramsPageSignature['Args']) {
    // @ts-expect-error - Owner type mismatch is ok here
    super(owner, args);
    this.setupMediaQuery();

    // Initialize chart dataset from controller query param (supports URL-based dataset selection)
    const controllerDataset = args.controller.dataset;
    if (controllerDataset) {
      this.chart.selectedDataset = controllerDataset;
    }
  }

  /***************************/
  /* Media Query Setup      */
  /***************************/

  private setupMediaQuery() {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    this._isLargeScreen = mediaQuery.matches;

    const handleMediaChange = (e: MediaQueryListEvent) => {
      this._isLargeScreen = e.matches;
    };

    mediaQuery.addEventListener('change', handleMediaChange);

    registerDestructor(this, () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
    });
  }

  get isLargeScreen() {
    return this._isLargeScreen;
  }

  /***************************/
  /* Program Templates      */
  /***************************/

  programTemplatesData = trackedFunction(this, async (): Promise<TasProgramTemplateModel[]> => {
    const companyId = this.args.company?.id;
    if (!companyId) {
      return [];
    }
    return await this.store.query<TasProgramTemplateModel>('tas-program-template', {
      company_id: companyId,
    });
  });

  get selectedProgramTemplate(): TasProgramTemplateModel | undefined {
    if (!this.args.controller.programTemplateId || !this.programTemplatesData.isFinished) {
      return undefined;
    }
    return this.programTemplatesData.value?.find(
      (template: TasProgramTemplateModel) => template.id === this.args.controller.programTemplateId
    );
  }

  /***************************/
  /* Partner Selection       */
  /***************************/

  get canFilterByPartner(): boolean {
    return Boolean(this.args.onCompanyChange) && Boolean(this.args.isAdminContext);
  }

  get shouldShowNonTasPartnerFilter(): boolean {
    return !this.isTasDataset && this.canFilterByPartner;
  }

  partnerCompaniesData = trackedFunction(this, async (): Promise<CompanyModel[]> => {
    if (!this.canFilterByPartner) {
      return [];
    }

    return this.store.query<CompanyModel>('company', {
      filter: { 'is-active': 1 },
      page: { limit: 400 },
    }) as Promise<CompanyModel[]>;
  });

  get partnerCompanyItems(): CompanyModel[] {
    return this.partnerCompaniesData.value || [];
  }

  partnerCompanyDidChange = (key: string | null) => {
    if (!this.args.onCompanyChange) {
      return;
    }

    const company = key ? this.partnerCompanyItems.find((item) => item.id === key) : undefined;
    this.args.onCompanyChange(company);
    this.clearCompanyScopedCustomColumns();
  };

  private isCompanyScopedCustomColumnKey(columnKey: string): boolean {
    const normalized = normalizeFieldKey(stripTypeSuffixes(columnKey));
    return (
      normalized.includes('customfields') ||
      normalized.includes('employeeeffields') ||
      normalized.includes('employeefields') ||
      normalized.endsWith('fieldsflattened')
    );
  }

  private clearCompanyScopedCustomColumns(): void {
    const selectedColumns = this.args.controller.selectedColumns?.split(',').filter(Boolean) || [];
    if (selectedColumns.length === 0) {
      return;
    }

    const remainingColumns = selectedColumns.filter(
      (columnKey) => !this.isCompanyScopedCustomColumnKey(columnKey)
    );

    if (remainingColumns.length !== selectedColumns.length) {
      this.setSelectedColumns(remainingColumns);
    }
  }

  private isTemplateScopedCustomColumnKey(columnKey: string): boolean {
    const normalized = normalizeFieldKey(stripTypeSuffixes(columnKey));
    return (
      normalized.includes('tasapplicationcustomfields') ||
      normalized.includes('tasprograminstancecustomfields') ||
      normalized.includes('tascoursecustomfields') ||
      normalized.includes('tasprogramtemplatecustomfields')
    );
  }

  private clearTemplateScopedCustomColumns(): void {
    const selectedColumns = this.args.controller.selectedColumns?.split(',').filter(Boolean) || [];
    if (selectedColumns.length === 0) {
      return;
    }

    const remainingColumns = selectedColumns.filter(
      (columnKey) => !this.isTemplateScopedCustomColumnKey(columnKey)
    );

    if (remainingColumns.length !== selectedColumns.length) {
      this.setSelectedColumns(remainingColumns);
    }
  }

  private syncDynamicCustomColumnScope(companyId?: string, programTemplateId?: string): void {
    if (!this.hasInitializedDynamicScope) {
      this.hasInitializedDynamicScope = true;
      this.previousDynamicScopeCompanyId = companyId;
      this.previousDynamicScopeProgramTemplateId = programTemplateId;
      return;
    }

    const companyChanged = this.previousDynamicScopeCompanyId !== companyId;
    const programTemplateChanged = this.previousDynamicScopeProgramTemplateId !== programTemplateId;

    if (companyChanged) {
      // Company changes can invalidate both employee custom and template custom keys.
      this.clearCompanyScopedCustomColumns();
    } else if (programTemplateChanged) {
      // Template changes should only invalidate template-scoped custom keys.
      this.clearTemplateScopedCustomColumns();
    }

    this.previousDynamicScopeCompanyId = companyId;
    this.previousDynamicScopeProgramTemplateId = programTemplateId;
  }

  /***************************/
  /* Sidebar Management     */
  /***************************/

  toggleSidebar = () => {
    this.isExpandedSidebar = !this.isExpandedSidebar;
  };

  /***************************/
  /* Drawer Tab Management  */
  /***************************/

  selectFiltersTab = () => {
    this.activeFilterTab = 'filters';
  };

  selectColumnsTab = () => {
    this.activeFilterTab = 'columns';
  };

  /***************************/
  /* Drawer Management      */
  /***************************/

  openDrawer = (type: string) => {
    this.selectedDrawerType = type;
    this.isDrawerOpen = true;
  };

  closeDrawer = () => {
    this.isDrawerOpen = false;
    this.selectedDrawerType = null;
  };

  /***************************/
  /* Responsive Drawer Props */
  /***************************/

  get drawerBackdrop() {
    return this.isLargeScreen ? 'none' : 'faded';
  }

  get drawerCloseOnOutsideClick() {
    // Reimagined Drawer always uses a full-screen overlay, so
    // click-outside-to-close should always be enabled.
    return true;
  }

  get mainContentClasses() {
    // The reimagined Drawer portals to <body>, so we no longer push the main
    // content aside with margin-right.  The old 'drawer-open' class added
    // margin-right: 448px which compressed the underlying table/chart.
    return 'main-content-area';
  }

  /***************************/
  /* Selected Filters       */
  /***************************/

  get selectedFilters() {
    return {
      'Program Name': this.selectedProgramTemplate?.programName || 'All',
      'Program Creation Data': this.args.controller.programCreatedAtStart
        ? `From ${this.args.controller.programCreatedAtStart || 'Any'} to ${
            this.args.controller.programCreatedAtEnd || 'Any'
          }`
        : null,
      'Application State': this.args.controller.applicationStates || 'All',
      'Payment State': this.args.controller.paymentStates || 'All',
      'Application Creation Date': this.args.controller.applicationCreatedAtStart
        ? `From ${this.args.controller.applicationCreatedAtStart} to ${
            this.args.controller.applicationCreatedAtEnd || ''
          }`
        : null,
      'Include Courses in the Report': this.args.controller.includeCourses ? 'Yes' : 'No',
      'Include Payments Year': this.args.controller.paymentYear || 'All',
      'Include Courses': this.args.controller.includeCourses ? 'Yes' : 'No',
      'Employee ID Filter': this.args.controller.employeeId || '',
      'Terminated Employees': this.args.controller.terminatedEmployees ? 'Yes' : 'No',
      'Exclude Term Verified': this.args.controller.excludeTermVerified ? 'Yes' : 'No',
      'Approver ID Filter': this.args.controller.approverId || null,
      'Program Template Filter': this.selectedProgramTemplate?.id || null,
      'Include Terminated Employees': this.args.controller.terminatedEmployees ? 'Yes' : null,
      'Include Invoice Dates': this.args.controller.includeInvoiceDate ? 'Yes' : null,
      'Course Cash Programs Only': this.args.controller.courseCashProgramsOnly ? 'Yes' : null,
    };
  }

  get hasActiveAdvancedFilters() {
    const controller = this.args.controller;
    return !!(
      controller.showIndividualApplications ||
      controller.applicationStates ||
      controller.applicationCreatedAtStart ||
      controller.applicationCreatedAtEnd ||
      controller.includePayments ||
      controller.paymentStates ||
      controller.paymentYear ||
      controller.includeCourses ||
      controller.coursesBeginDate ||
      controller.coursesEndDate ||
      controller.employeeId ||
      controller.terminatedEmployees ||
      controller.excludeTermVerified ||
      controller.approverId ||
      controller.includeInvoiceDate ||
      controller.excludeNonVoucherPrograms ||
      controller.courseCashProgramsOnly
    );
  }

  /***************************/
  /* Column Selection       */
  /***************************/

  private isObjectLikeFieldType(type?: string): boolean {
    if (!type) {
      return false;
    }

    const normalized = type.toLowerCase();
    return normalized === 'object' || normalized === 'flat_object' || normalized === 'nested';
  }

  private mapReportDatatypeToFieldType(reportDatatype?: string): string {
    if (!reportDatatype) {
      return 'keyword';
    }

    const normalized = reportDatatype.toLowerCase();
    if (RANGE_QUERY_TYPES.has(normalized)) {
      return normalized;
    }

    if (normalized === 'boolean') {
      return 'boolean';
    }

    return 'keyword';
  }

  // Fetch OpenSearch mapping dynamically to discover available fields
  mappingData = trackedFunction(this, async (): Promise<FieldOption[]> => {
    const searchAdapter = this.store?.adapterFor?.('search') as
      | SearchAdapterWithEmployeeCustomMappings
      | undefined;

    const companyId = this.args.company?.id || undefined;
    const programTemplateId = this.args.controller.programTemplateId || undefined;

    this.syncDynamicCustomColumnScope(companyId, programTemplateId);

    if (!searchAdapter) {
      return OpenSearchFieldMapper.getFallbackFields(this.chart.selectedDataset).map((field) => ({
        ...field,
        group: field.group || this.inferFieldGroup(field.key),
      }));
    }

    try {
      const modelType = getModelTypeFromDataset(this.chart.selectedDataset);

      const fieldsResponse: SearchFieldsResponse = (await searchAdapter.fields?.(
        modelType,
        companyId,
        programTemplateId
      )) || { fields: [] };

      const {
        fields: parsedFields,
        sourceFieldByFieldKey,
        apiLabelByFieldKey,
      } = this.buildFieldOptionsFromFieldsResponse(fieldsResponse);

      let employeeCustomMappings: EmployeeCustomFieldMapping[] = [];
      if (companyId) {
        try {
          const employeeMappingsResponse = await searchAdapter.employeeCustomFieldMappings?.(
            companyId,
            programTemplateId
          );
          employeeCustomMappings = employeeMappingsResponse?.mappings || [];
        } catch {
          employeeCustomMappings = [];
        }
      }

      let companyEfKeyLabelMap: Record<string, string> = {};
      let templateKeyLabelMap: Record<string, string> = {};
      let templateKeyProvinceMap: Record<string, string> = {};

      if (companyId || programTemplateId) {
        try {
          const mappingWithLabelMaps: SearchMappingWithLabelMapsResponse =
            (await searchAdapter.mappingWithLabelMaps?.(modelType, companyId, programTemplateId)) ||
            {};

          const labelMaps = mappingWithLabelMaps.labelMaps || {};

          companyEfKeyLabelMap = labelMaps.companyEfKeyLabelMap || {};
          templateKeyLabelMap = labelMaps.tasTemplateKeyLabelMap || {};
          templateKeyProvinceMap = labelMaps.tasTemplateKeyProvinceMap || {};
        } catch {
          companyEfKeyLabelMap = {};
          templateKeyLabelMap = {};
          templateKeyProvinceMap = {};
        }
      }

      const fields = applyTemplateAndCustomFieldLabels(
        parsedFields,
        sourceFieldByFieldKey,
        apiLabelByFieldKey,
        companyEfKeyLabelMap,
        templateKeyLabelMap,
        templateKeyProvinceMap
      );

      // Normalize post-resolved labels (including API/label-map overrides)
      // so pluralized variants dedupe consistently in the selector.
      const normalizedLabelFields = fields.map((field) => ({
        ...field,
        label: this.normalizeDisplayLabel(field.key, field.label, field.group) || field.label,
      }));

      const fieldsWithEmployeeCustoms = this.appendEmployeeCustomFieldOptions(
        normalizedLabelFields,
        employeeCustomMappings
      );

      const fieldsWithoutObjectRoots = fieldsWithEmployeeCustoms.filter(
        (field) =>
          !this.isEmployeeCustomRootFieldKey(field.key) && !this.isObjectLikeFieldType(field.type)
      );

      const deduplicatedFinalFields = this.dedupeFieldOptions(fieldsWithoutObjectRoots);

      if (deduplicatedFinalFields.length === 0) {
        throw new Error(`No fields found for ${modelType}`);
      }

      return deduplicatedFinalFields;
    } catch (error) {
      console.error('Error fetching field data:', error);
      this.notifications.add('Failed to load column mappings from server', {
        appearance: 'error',
      });

      try {
        const modelType = getModelTypeFromDataset(this.chart.selectedDataset);
        const mapping = await searchAdapter.mapping?.(modelType);
        if (mapping) {
          return OpenSearchFieldMapper.parseMapping(mapping);
        }
      } catch (mappingError) {
        console.error('Error fetching mapping fallback:', mappingError);
      }

      return OpenSearchFieldMapper.getFallbackFields(this.chart.selectedDataset).map((field) => ({
        ...field,
        group: field.group || this.inferFieldGroup(field.key),
      }));
    }
  });

  private buildFieldOptionsFromFieldsResponse(response: SearchFieldsResponse): ParsedFieldsResult {
    const fields = response.fields || [];
    const sourceFieldByFieldKey: Record<string, string> = {};
    const apiLabelByFieldKey: Record<string, string> = {};

    const parsedFields = fields
      .filter((field) => Boolean(field.name))
      .filter((field) => !this.isObjectLikeFieldType(field.type))
      .filter((field) => !this.isEmployeeCustomRootFieldKey(field.name))
      .filter((field) => !this.isInternalFlatObjectLeafField(field.name))
      // Filter out nested object subfields (e.g. employees_nested.employee_name_keyword)
      // that can't render as flat table columns. Keep custom field dot-paths
      // (ef_fields, custom_fields) which ARE valid flat columns.
      .filter((field) => {
        if (!field.name.includes('.')) return true;
        const parent = field.name.split('.')[0] || '';
        const normalizedParent = parent.replace(/_/g, '').toLowerCase();
        return (
          normalizedParent.includes('customfields') ||
          normalizedParent.includes('effields') ||
          normalizedParent.includes('employeefields')
        );
      })
      .map((field) => {
        const rawName = field.name;
        const name = this.stripInternalFlatObjectLeafSegment(rawName);
        const type = field.type || 'keyword';
        const explicitComponentHint =
          (field as SearchFieldPayload & { uiHint?: { componentType?: string } }).uiHint
            ?.componentType ||
          (field as SearchFieldPayload & { uiHints?: { componentType?: string } }).uiHints
            ?.componentType;
        const componentTypeHint = OpenSearchFieldMapper.resolveComponentTypeHint(
          name,
          explicitComponentHint
        );
        const sourceField =
          field.sourceField || (field as { source_field?: string | undefined }).source_field;
        const sanitizedApiLabel = this.sanitizeApiLabel(name, field.label);
        if (sourceField) {
          sourceFieldByFieldKey[name] = sourceField;
        }
        if (sanitizedApiLabel) {
          apiLabelByFieldKey[name] = sanitizedApiLabel;
        }

        const capabilities = field.capabilities;
        const uiHints = field.uiHints;
        const group = this.inferDisplayGroup(name, field.isCustom);

        const isObjectLike = this.isObjectLikeFieldType(type);
        const inferredRangeQueryable = RANGE_QUERY_TYPES.has(type.toLowerCase());

        return {
          key: name,
          label: this.resolveColumnLabel(name, sanitizedApiLabel, group),
          type,
          group,
          isDate: type === 'date',
          isTimestamp: type !== 'date' && OpenSearchFieldMapper.isTimestampField(name),
          adminOnly: OpenSearchFieldMapper.isAdminOnlyField(name),
          // Map backend capabilities to FieldOption flags
          isFilterable: capabilities?.filter ?? !isObjectLike,
          isSortable: capabilities?.sort ?? false,
          isGroupable: capabilities?.aggregate ?? false,
          isRangeQueryable: capabilities?.range ?? inferredRangeQueryable,
          isFullTextSearchable: capabilities?.fullText ?? type === 'text',
          isCustomField: field.isCustom,
          semanticRole: field.semanticRole,
          reportDatatype: field.reportDatatype ?? type,
          enumValues: field.enumValues,
          // UI rendering hints from backend
          uiHints: uiHints
            ? {
                filterWidget: uiHints.filterWidget,
                multiSelect: uiHints.multiSelect,
              }
            : undefined,
          ...(componentTypeHint ? { componentTypeHint } : {}),
        };
      })
      .filter((field) => {
        if (!this.isEmployeeCustomFieldKey(field.key)) {
          return true;
        }

        const sourceField =
          sourceFieldByFieldKey[field.key] ||
          extractEmployeeSourceField(field.key) ||
          this.inferEmployeeSourceFieldFromKey(field.key) ||
          undefined;

        return !this.isGlobalEmployeeSourceField(sourceField);
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    const deduplicatedFields = this.dedupeFieldOptions(parsedFields);

    // Apply FE-defined enum values for known fields that the backend
    // doesn't populate (e.g., PSLF form_status, SYF enrollment_state).
    // Backend-provided enumValues always take precedence.
    const enrichedFields = applyFallbackEnumValues(deduplicatedFields, this.chart.selectedDataset);

    return {
      fields: enrichedFields,
      sourceFieldByFieldKey,
      apiLabelByFieldKey,
    };
  }

  private inferDisplayGroup(fieldName: string, isCustomField?: boolean): string {
    const modelType = getModelTypeFromDataset(this.chart.selectedDataset);
    return sharedGetDisplayGroup(fieldName, modelType, isCustomField);
  }

  /**
   * Delegates to the shared getFieldGroup utility in label-generation.ts,
   * passing the current dataset's model type for dataset-aware grouping.
   */
  private inferFieldGroup(fieldName: string): string {
    const modelType = getModelTypeFromDataset(this.chart.selectedDataset);
    return sharedGetFieldGroup(fieldName, modelType);
  }

  private appendEmployeeCustomFieldOptions(
    fields: FieldOption[],
    mappings: EmployeeCustomFieldMapping[]
  ): FieldOption[] {
    if (!mappings.length) {
      return fields;
    }

    const byKey = new Map(
      fields.map((field) => [normalizeFieldKey(stripTypeSuffixes(field.key)), field] as const)
    );
    const employeeCustomCanonicalKeys = new Set(
      fields
        .map((field) => this.employeeCustomCanonicalKey(field.key))
        .filter((value): value is string => Boolean(value))
    );
    const enriched = [...fields];

    mappings.forEach((mapping) => {
      const rawFieldName = mapping.fieldName;
      const label = mapping.label;
      if (!rawFieldName || !label) {
        return;
      }

      if (this.isGlobalEmployeeSourceField(mapping.sourceField)) {
        return;
      }

      const key = this.resolveEmployeeCustomMappingFieldKey(rawFieldName);
      if (
        this.isEmployeeCustomRootFieldKey(key) ||
        this.isObjectLikeFieldType(mapping.reportDatatype)
      ) {
        return;
      }

      const normalizedKey = normalizeFieldKey(stripTypeSuffixes(key));
      if (byKey.has(normalizedKey)) {
        return;
      }

      const canonicalKey = this.employeeCustomCanonicalKey(key, mapping.sourceField);
      if (canonicalKey && employeeCustomCanonicalKeys.has(canonicalKey)) {
        return;
      }

      const mappedType = this.mapReportDatatypeToFieldType(mapping.reportDatatype);
      const syntheticField: FieldOption = {
        key,
        label,
        type: mappedType,
        group: this.inferDisplayGroup(key, true),
        isDate: mappedType === 'date',
        isTimestamp: false,
        isFilterable: true,
        isSortable: false,
        isGroupable: true,
        isRangeQueryable: RANGE_QUERY_TYPES.has(mappedType),
        isFullTextSearchable: mappedType === 'text',
        isCustomField: true,
        reportDatatype: mapping.reportDatatype || mappedType,
        enumValues: mapping.enumValues,
      };

      enriched.push(syntheticField);
      byKey.set(normalizedKey, syntheticField);
      if (canonicalKey) {
        employeeCustomCanonicalKeys.add(canonicalKey);
      }
    });

    return enriched;
  }

  private resolveEmployeeCustomMappingFieldKey(rawFieldName: string): string {
    const camelized = this.camelizeFieldPath(rawFieldName);
    const normalizedDataset = this.chart.selectedDataset;
    const leaf = stripTypeSuffixes(camelized.split('.').filter(Boolean).at(-1) || camelized);

    if (normalizedDataset === 'people') {
      return `allEmployeeCustomFieldsFlattened.${leaf}`;
    }
    if (
      normalizedDataset === 'syf-match-participants' ||
      normalizedDataset === 'slr-contribution-plan-participants'
    ) {
      return `employeeEfFields.employeeCustomFields.${leaf}`;
    }

    return camelized;
  }

  private camelizeFieldPath(fieldPath: string): string {
    return fieldPath
      .split('.')
      .filter(Boolean)
      .map((segment) => segment.replace(/_([a-z])/g, (_match, letter) => letter.toUpperCase()))
      .join('.');
  }

  private isEmployeeCustomRootFieldKey(fieldKey: string): boolean {
    const normalized = this.canonicalEmployeeContainerKey(fieldKey);
    if (
      normalized === 'employeeeffields' ||
      normalized === 'employeecustomfields' ||
      normalized === 'employeefields'
    ) {
      return true;
    }

    if (!fieldKey.includes('.')) {
      return false;
    }

    const segments = fieldKey
      .split('.')
      .filter(Boolean)
      .map((segment) => {
        const compact = segment.replace(/_/g, '').toLowerCase();
        return compact.startsWith('all') ? compact.slice(3) : compact;
      });

    const leaf = segments.at(-1);
    const parent = segments.at(-2);
    if (!leaf || !parent) {
      return false;
    }

    const isContainerLeaf = leaf === 'employeecustomfields' || leaf === 'employeefields';
    const isEmployeeContainerParent =
      parent === 'employeeeffields' ||
      parent === 'employeecustomfields' ||
      parent === 'employeefields';

    return isContainerLeaf && isEmployeeContainerParent;
  }

  private isEmployeeCustomFieldKey(fieldKey: string): boolean {
    const compact = fieldKey.replace(/_/g, '').toLowerCase();
    const [parent] = compact.split('.');
    if (!parent) {
      return false;
    }

    const canonicalParent = parent.startsWith('all') ? parent.slice(3) : parent;
    return (
      canonicalParent === 'employeeeffields' ||
      canonicalParent === 'employeecustomfields' ||
      canonicalParent === 'employeefields'
    );
  }

  private isInternalFlatObjectLeafField(fieldKey: string): boolean {
    if (!fieldKey.includes('.')) {
      return false;
    }

    const segments = fieldKey.split('.').filter(Boolean);
    if (segments.length < 2) {
      return false;
    }

    const parentRaw = segments[0]?.replace(/_/g, '').toLowerCase() || '';
    const parent = parentRaw.startsWith('all') ? parentRaw.slice(3) : parentRaw;
    const isCustomParent =
      parent.includes('customfields') ||
      parent.includes('effields') ||
      parent.includes('employeefields') ||
      parent.includes('flattened');

    if (!isCustomParent) {
      return false;
    }

    const leaf = segments.at(-1)?.toLowerCase();
    return leaf ? INTERNAL_FLAT_OBJECT_LEAF_SEGMENTS.has(leaf) : false;
  }

  private stripInternalFlatObjectLeafSegment(fieldKey: string): string {
    if (!this.isInternalFlatObjectLeafField(fieldKey)) {
      return fieldKey;
    }

    const segments = fieldKey.split('.').filter(Boolean);
    return segments.slice(0, -1).join('.');
  }

  private sanitizeApiLabel(fieldKey: string, apiLabel?: string): string | undefined {
    const trimmed = apiLabel?.trim();
    if (!trimmed) {
      return undefined;
    }

    // Employee custom labels that collapse to a single letter are typically
    // flat_object internals leaking through label maps (e.g. "S").
    if (
      trimmed.length === 1 &&
      (this.isEmployeeCustomFieldKey(fieldKey) ||
        this.isInternalFlatObjectLeafField(fieldKey) ||
        normalizeFieldKey(fieldKey).includes('flattened'))
    ) {
      return undefined;
    }

    return trimmed.replace(/\s+/g, ' ');
  }

  private normalizeTechnicalLabelSuffixes(label: string): string {
    return label
      .replace(/(?:\s+(?:keyword|analyzable|text|nested|flattened|flat\s*object))+$/i, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private applyWordCase(template: string, value: string): string {
    if (template === template.toUpperCase()) {
      return value.toUpperCase();
    }

    if (template[0] && template[0] === template[0].toUpperCase()) {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }

    return value;
  }

  private singularizeTrailingWord(word: string): string {
    const lower = word.toLowerCase();

    // Keep common singular endings that happen to end in "s".
    if (lower.endsWith('ss') || lower.endsWith('us') || lower.endsWith('is')) {
      return word;
    }

    let singular = lower;
    if (lower.endsWith('ies') && lower.length > 3) {
      singular = `${lower.slice(0, -3)}y`;
    } else if (/(xes|zes|ches|shes|sses)$/.test(lower)) {
      singular = lower.slice(0, -2);
    } else if (lower.endsWith('s') && lower.length > 1) {
      singular = lower.slice(0, -1);
    }

    return this.applyWordCase(word, singular);
  }

  private normalizeNamePlural(label: string): string {
    return label.replace(/\b([A-Za-z]+)\b$/, (match, word: string) => {
      return this.singularizeTrailingWord(word || match);
    });
  }

  private shouldDropEmployeePrefix(fieldKey: string, group?: string): boolean {
    const normalizedGroup = (group || '').toLowerCase();
    if (normalizedGroup.startsWith('employee')) {
      return true;
    }

    return this.isEmployeeCustomFieldKey(fieldKey) || /(^|[._])employee/i.test(fieldKey);
  }

  private normalizeDisplayLabel(fieldKey: string, label: string, group?: string): string {
    let normalized = label.replace(/\s+/g, ' ').trim();
    if (!normalized) {
      return normalized;
    }

    normalized = this.normalizeTechnicalLabelSuffixes(normalized);

    if (this.shouldDropEmployeePrefix(fieldKey, group)) {
      normalized = normalized.replace(/^Employee\s+/i, '').trim();
    }

    normalized = this.normalizeNamePlural(normalized);
    return normalized.replace(/\s+/g, ' ').trim();
  }

  private resolveColumnLabel(fieldKey: string, apiLabel?: string, group?: string): string {
    const cleanApiLabel = this.sanitizeApiLabel(fieldKey, apiLabel);
    if (cleanApiLabel) {
      const normalizedApiLabel = this.normalizeDisplayLabel(fieldKey, cleanApiLabel, group);
      if (normalizedApiLabel) {
        return normalizedApiLabel;
      }
    }

    const displayKey = this.stripInternalFlatObjectLeafSegment(fieldKey);
    const generated = OpenSearchFieldMapper.formatFieldLabel(displayKey).trim();
    if (generated) {
      const normalizedGeneratedLabel = this.normalizeDisplayLabel(fieldKey, generated, group);
      if (normalizedGeneratedLabel) {
        return normalizedGeneratedLabel;
      }
    }

    const leaf = displayKey.split('.').filter(Boolean).at(-1);
    const fallback = this.normalizeDisplayLabel(
      fieldKey,
      (leaf || fieldKey).replace(/[_-]+/g, ' ').trim(),
      group
    );
    return fallback || fieldKey;
  }

  private isGlobalEmployeeSourceField(sourceField?: string): boolean {
    if (!sourceField) {
      return false;
    }

    const normalizedSource = this.normalizeEmployeeSourceField(sourceField);
    return GLOBAL_EMPLOYEE_SOURCE_FIELDS.has(normalizedSource);
  }

  private canonicalEmployeeContainerKey(fieldKey: string): string {
    const normalized = normalizeFieldKey(stripTypeSuffixes(fieldKey));
    return normalized.startsWith('all') ? normalized.slice(3) : normalized;
  }

  private normalizeEmployeeSourceField(sourceField: string): string {
    const stripped = stripTypeSuffixes(sourceField);
    const leaf = stripped.split('.').filter(Boolean).at(-1) || stripped;
    const withoutEmployeePrefix = leaf
      .replace(/^employee_/i, '')
      .replace(/^employee(?=[A-Z])/i, '');

    return withoutEmployeePrefix
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/[^a-z0-9_]/gi, '_')
      .toLowerCase()
      .replace(/^_+|_+$/g, '');
  }

  private inferEmployeeSourceFieldFromKey(fieldKey: string): string | null {
    if (!this.isEmployeeCustomFieldKey(fieldKey)) {
      return null;
    }

    const leaf = fieldKey.split('.').filter(Boolean).at(-1);
    if (!leaf) {
      return null;
    }

    return stripTypeSuffixes(leaf);
  }

  private employeeCustomCanonicalKey(fieldKey: string, sourceField?: string): string | null {
    if (!this.isEmployeeCustomFieldKey(fieldKey)) {
      return null;
    }

    const canonicalSourceField =
      sourceField ||
      extractEmployeeSourceField(fieldKey) ||
      this.inferEmployeeSourceFieldFromKey(fieldKey) ||
      undefined;
    if (canonicalSourceField) {
      return `source:${canonicalSourceField.toLowerCase()}`;
    }

    return `key:${normalizeFieldKey(stripTypeSuffixes(fieldKey))}`;
  }

  private dedupeFieldOptions(fields: FieldOption[]): FieldOption[] {
    const byNormalizedKey = new Map<string, FieldOption>();
    const byNormalizedLabel = new Map<string, FieldOption>();
    const byEmployeeCustomCanonical = new Map<string, string>();

    for (const field of fields) {
      const normalizedKey = normalizeFieldKey(stripTypeSuffixes(field.key));
      if (byNormalizedKey.has(normalizedKey)) {
        continue;
      }

      const canonical = this.employeeCustomCanonicalKey(field.key);
      if (canonical) {
        const existingNormalizedKey = byEmployeeCustomCanonical.get(canonical);
        if (existingNormalizedKey) {
          continue;
        }
        byEmployeeCustomCanonical.set(canonical, normalizedKey);
      }

      byNormalizedKey.set(normalizedKey, field);

      const normalizedLabel = this.labelDedupKey(field);
      if (normalizedLabel) {
        const existingByLabel = byNormalizedLabel.get(normalizedLabel);
        if (existingByLabel) {
          byNormalizedLabel.set(
            normalizedLabel,
            this.preferredFieldForDuplicateLabel(existingByLabel, field)
          );
        } else {
          byNormalizedLabel.set(normalizedLabel, field);
        }
      }
    }

    if (byNormalizedLabel.size === 0) {
      return Array.from(byNormalizedKey.values());
    }

    const deduped = new Set(byNormalizedLabel.values());
    return Array.from(byNormalizedKey.values()).filter((field) => deduped.has(field));
  }

  private labelDedupKey(field: FieldOption): string {
    const singularLabel = this.normalizeNamePlural(field.label.trim());
    const normalizedLabel = singularLabel.toLowerCase().replace(/\s+/g, ' ');
    if (!normalizedLabel) {
      return '';
    }

    const baseGroup = (field.group || 'other')
      .replace(/\s*\/\s*custom$/i, '')
      .trim()
      .toLowerCase();

    return `${baseGroup}::${normalizedLabel}`;
  }

  private preferredFieldForDuplicateLabel(
    current: FieldOption,
    candidate: FieldOption
  ): FieldOption {
    const currentRank = this.duplicateLabelPreferenceRank(current);
    const candidateRank = this.duplicateLabelPreferenceRank(candidate);

    if (candidateRank < currentRank) {
      return candidate;
    }

    return current;
  }

  private duplicateLabelPreferenceRank(field: FieldOption): number {
    const strippedKey = stripTypeSuffixes(field.key);
    const leaf = strippedKey.split('.').filter(Boolean).at(-1)?.toLowerCase();
    const isGenericLeaf = Boolean(leaf && GENERIC_LEAF_FIELD_NAMES.has(leaf));
    const depth = strippedKey.split('.').filter(Boolean).length;

    // Prefer less-generic fields (e.g. companyId over id), shallower paths,
    // and shorter canonical keys when duplicate labels collide.
    const genericPenalty = isGenericLeaf ? 1000 : 0;
    return genericPenalty + depth * 100 + strippedKey.length;
  }

  get availableFields(): FieldOption[] {
    // Show error state if mapping failed to load
    if (this.mappingData.error) {
      return [];
    }

    // Show loading state
    if (this.mappingData.isLoading || !this.mappingData.value) {
      return [];
    }

    // Get fields from mapping
    const baseFields = this.mappingData.value;
    const isTasDataset = this.isTasDataset;

    // Deprecated fields to exclude from column selector
    // These fields are no longer populated or are being phased out
    // The backend filters these from the mapping endpoint in snake_case,
    // but we check camelCase here for defense-in-depth or cached responses
    const deprecatedFields = new Set([
      'tasApplicationCoursesBeginDate',
      'tasApplicationCoursesEndDate',
      'tasProgramInstanceFieldLockBalanceMultiplierAtStartOfFiscalYearBoolean',
      'paymentFirstDate', // Payment date fields should use paymentLastDate (Payment Date)
      // Add other deprecated fields here as needed
    ]);

    // Filter based on aggregation mode
    // These fields only exist in aggregated responses, not in individual documents
    const aggregateOnlyFields = new Set(['total_applications', 'applications']);
    const paymentFieldPatterns = [/payment/i];

    // Determine if we should show admin-only fields (default to false if not specified)
    const isAdminContext = this.args.isAdminContext ?? false;

    if (!isTasDataset) {
      return baseFields
        .filter((field) => isAdminContext || !field.adminOnly)
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    if (this.effectiveShowIndividualApplications) {
      // Individual mode: exclude aggregate-only fields, deprecated fields, and admin-only fields if not in admin context
      return baseFields.filter(
        (field) =>
          !aggregateOnlyFields.has(field.key) &&
          !deprecatedFields.has(field.key) &&
          (isAdminContext || !field.adminOnly) // Filter out admin-only fields if not in admin context
      );
    } else {
      // Aggregate mode: exclude deprecated fields and admin-only fields if not in admin context
      const filteredFields = baseFields.filter(
        (field) =>
          !deprecatedFields.has(field.key) &&
          !paymentFieldPatterns.some((pattern) => pattern.test(field.key)) &&
          (isAdminContext || !field.adminOnly) // Filter out admin-only fields if not in admin context
      );

      // Note: aggregate-only fields like 'total_applications' come from the aggregation response,
      // not from the OpenSearch mapping, so they won't be in the mapping data
      // We need to manually add them if they're not already present
      const hasAggregateFields = filteredFields.some((f) => aggregateOnlyFields.has(f.key));

      if (!hasAggregateFields) {
        return [
          ...filteredFields,
          { key: 'total_applications', label: 'Total Applications', type: 'text', group: 'Other' },
          { key: 'applications', label: 'Applications Count', type: 'text', group: 'Other' },
        ].sort((a, b) => a.label.localeCompare(b.label));
      }

      return filteredFields.sort((a, b) => a.label.localeCompare(b.label));
    }
  }

  get selectedColumnsArray(): string[] {
    const explicitColumns = this.args.controller.selectedColumns?.split(',').filter(Boolean) || [];

    // When no columns are explicitly selected, use per-dataset defaults.
    // This prevents the table from showing ALL columns (overwhelming) and
    // ensures each dataset starts with a sensible set of visible columns.
    let columns =
      explicitColumns.length > 0 ? explicitColumns : this.getDefaultColumnsForCurrentDataset();

    if (!this.effectiveShowIndividualApplications) {
      columns = columns.filter((column) => !/payment/i.test(column));
    } else if (columns.length === 0) {
      // For non-TAS datasets with no explicit column selection, use curated defaults
      // so the table doesn't dump every field from the index.
      const dataset = this.chart.selectedDataset || 'tas-program-instances';
      const defaults = getDefaultColumns(dataset);
      if (defaults.length > 0) {
        columns = defaults;
      }
    }

    // Collapse semantic duplicates (e.g. plural/singular variants) to a single
    // canonical key so table/query rendering never shows all variants at once.
    columns = this.canonicalizeSelectedColumns(columns);

    // Return the cached reference when the column list is materially unchanged.
    // This prevents the data provider's trackedFunction from seeing a new array
    // reference and re-firing the search query unnecessarily.
    const prev = this._prevSelectedColumns;
    if (columns.length === prev.length && columns.every((col, i) => col === prev[i])) {
      return prev;
    }

    this._prevSelectedColumns = columns;
    return columns;
  }

  /**
   * Get default columns for the current dataset, filtered to only include
   * columns that actually exist in the available fields.
   *
   * Matching is suffix-agnostic: type suffixes like `_keyword`, `_date`, etc.
   * are stripped from both the default keys and the available field keys before
   * comparison.  This ensures defaults work regardless of whether the available
   * fields come from the fields API (suffixed), the mapping endpoint, or the
   * static fallback (unsuffixed).
   *
   * If none of the hard-coded defaults match, the first N available fields are
   * returned so the table always starts with a manageable column set.
   */
  private getDefaultColumnsForCurrentDataset(): string[] {
    // Computed aggregation columns are added unconditionally by the aggregate
    // table, so they must not appear in the selectable defaults.
    const AGGREGATION_KEYS = Object.keys(AGGREGATION_COLUMNS);

    // For TAS aggregate mode, use aggregate-specific data columns so the
    // column selector reflects what the aggregate table actually displays.
    const defaults =
      this.isTasDataset && !this.effectiveShowIndividualApplications
        ? DEFAULT_COLUMNS.aggregate.filter((key) => !AGGREGATION_KEYS.includes(key))
        : getDefaultColumns(this.chart.selectedDataset);
    const available = this.availableFields;

    // If fields haven't loaded yet, return the raw defaults
    if (available.length === 0) {
      return defaults;
    }

    // Build a map from normalised (suffix-stripped) key → actual available field key
    const availableByNorm = new Map<string, string>();
    for (const f of available) {
      const norm = normalizeFieldKey(stripTypeSuffixes(f.key));
      if (!availableByNorm.has(norm)) {
        availableByNorm.set(norm, f.key);
      }
    }

    // Match defaults against available fields using suffix-stripped normalisation
    const matched = defaults
      .map((key) => availableByNorm.get(normalizeFieldKey(stripTypeSuffixes(key))))
      .filter((key): key is string => key !== undefined);

    if (matched.length > 0) {
      return matched;
    }

    // No defaults matched — pick first N available fields as a reasonable starting set
    const MAX_DEFAULT_COLUMNS = 6;
    return available.slice(0, MAX_DEFAULT_COLUMNS).map((f) => f.key);
  }

  private get availableFieldByNormalizedKey(): Map<string, FieldOption> {
    const byKey = new Map<string, FieldOption>();

    for (const field of this.availableFields) {
      const normalized = normalizeFieldKey(stripTypeSuffixes(field.key));
      if (!byKey.has(normalized)) {
        byKey.set(normalized, field);
      }
    }

    return byKey;
  }

  private get selectedColumnRepresentativeByFamily(): Map<string, string> {
    const byFamily = new Map<string, string>();

    for (const field of this.availableFields) {
      const family = this.labelDedupKey(field);
      if (family && !byFamily.has(family)) {
        byFamily.set(family, field.key);
      }
    }

    return byFamily;
  }

  private selectedColumnFamilyKey(columnKey: string): string {
    const normalizedKey = normalizeFieldKey(stripTypeSuffixes(columnKey));
    const mappedField = this.availableFieldByNormalizedKey.get(normalizedKey);

    if (mappedField) {
      const mappedFamily = this.labelDedupKey(mappedField);
      if (mappedFamily) {
        return mappedFamily;
      }
    }

    const inferredGroup = this.inferFieldGroup(columnKey);
    const inferredLabel = this.resolveColumnLabel(columnKey, undefined, inferredGroup);
    const syntheticField: FieldOption = {
      key: columnKey,
      label: inferredLabel,
      type: 'keyword',
      group: inferredGroup,
    };

    const inferredFamily = this.labelDedupKey(syntheticField);
    return inferredFamily || `key::${normalizedKey}`;
  }

  private canonicalizeSelectedColumns(columns: string[]): string[] {
    if (columns.length <= 1) {
      return columns;
    }

    const representativeByFamily = this.selectedColumnRepresentativeByFamily;
    const seenFamilies = new Set<string>();
    const seenNormalizedKeys = new Set<string>();
    const canonical: string[] = [];

    for (const rawColumn of columns) {
      const family = this.selectedColumnFamilyKey(rawColumn);
      if (seenFamilies.has(family)) {
        continue;
      }

      const preferredColumn = representativeByFamily.get(family) || rawColumn;
      const normalizedPreferred = normalizeFieldKey(stripTypeSuffixes(preferredColumn));
      if (seenNormalizedKeys.has(normalizedPreferred)) {
        seenFamilies.add(family);
        continue;
      }

      seenFamilies.add(family);
      seenNormalizedKeys.add(normalizedPreferred);
      canonical.push(preferredColumn);
    }

    return canonical;
  }

  setSelectedColumns = (columns: string[]) => {
    this.args.controller.selectedColumns = this.canonicalizeSelectedColumns(columns).join(',');
  };

  private get normalizedSelectedColumnsSet() {
    const key = this.selectedColumnsArray.join('\u0000');

    if (key === this._prevNormalizedSelectedColumnsKey) {
      return this._prevNormalizedSelectedColumns;
    }

    this._prevNormalizedSelectedColumnsKey = key;
    this._prevNormalizedSelectedColumns = new Set(
      this.selectedColumnsArray.map((column) => normalizeFieldKey(column))
    );

    return this._prevNormalizedSelectedColumns;
  }

  /***************************/
  /* Filter Management      */
  /***************************/

  updateQueryParameters = (updates: Record<string, unknown>) => {
    this.args.controller.setProperties(updates);
    this.autoEnableColumnsForFilters(updates);
  };

  /**
   * Update generic filters for non-TAS datasets.
   * Merges partial updates into the tracked filters object and resets to page 1.
   */
  updateGenericFilters = (updates: Record<string, unknown>) => {
    this.genericFilters = { ...this.genericFilters, ...updates };
    this.args.controller.setProperties({ page: 1 });
  };

  /**
   * Clear all generic filters.
   */
  clearGenericFilters = () => {
    this.genericFilters = {};
    this.args.controller.setProperties({ page: 1 });
  };

  /**
   * Get the list of filterable fields from the current dataset's available fields.
   */
  get filterableFields(): FieldOption[] {
    return this.availableFields.filter((f) => f.isFilterable);
  }

  /**
   * Default filter-to-column dependencies — auto-derived from the filter registry.
   * Can be overridden by passing @filterColumnDependencies.
   */
  private get filterColumnDependencies(): Record<string, string[]> {
    return this.args.filterColumnDependencies ?? deriveFilterColumnDependencies();
  }

  /**
   * Flat list of field keys available in the current dataset.
   *
   * Used to determine which filter sections are relevant — sections whose
   * field patterns don't match any dataset field are hidden entirely.
   * This is intentionally the *dataset* fields (what the backend provides),
   * not the user's *selected* columns. Column-level protection is handled
   * separately by the filter-column dependency modals.
   */
  get availableFieldKeys(): string[] {
    return this.availableFields.map((f) => f.key);
  }

  private getControllerValue = <K extends keyof TasProgramsControllerInterface>(
    key: K
  ): TasProgramsControllerInterface[K] => this.args.controller[key];

  private autoEnableColumnsForFilters(filterUpdates: Record<string, unknown>) {
    const currentColumns = new Set(this.selectedColumnsArray);
    let columnsAdded = false;

    Object.entries(filterUpdates).forEach(([filterKey, filterValue]) => {
      const isFilterActive = this.hasActiveFilterValue(filterValue);

      // Special handling for paymentStates
      if (filterKey === 'paymentStates' && isFilterActive && typeof filterValue === 'string') {
        const selectedStates = filterValue.split(',').filter(Boolean);
        selectedStates.forEach((state) => {
          if (!currentColumns.has(state)) {
            currentColumns.add(state);
            columnsAdded = true;
          }
        });
        return;
      }

      // Standard handling
      const requiredColumns = this.filterColumnDependencies[filterKey];
      if (requiredColumns && isFilterActive) {
        requiredColumns.forEach((columnKey: string) => {
          if (!currentColumns.has(columnKey)) {
            currentColumns.add(columnKey);
            columnsAdded = true;
          }
        });
      }
    });

    if (columnsAdded) {
      this.setSelectedColumns(Array.from(currentColumns));
    }
  }

  private hasActiveFilterValue(value: unknown): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    if (typeof value === 'boolean') {
      return value === true;
    }
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return true;
  }

  /***************************/
  /* Column Selection UI    */
  /***************************/

  /**
   * Check if a field is selected, handling both camelCase and snake_case formats
   */
  isColumnSelected = (fieldKey: string) => {
    return this.normalizedSelectedColumnsSet.has(normalizeFieldKey(fieldKey));
  };

  /**
   * Get list of active filters that depend on a specific column
   */
  private getFiltersRequiringColumn = (columnKey: string): string[] => {
    const normalizedColumnKey = normalizeFieldKey(columnKey);
    const affectedFilters: string[] = [];

    Object.entries(this.filterColumnDependencies).forEach(([filterKey, requiredColumns]) => {
      const controllerKey = filterKey as keyof TasProgramsControllerInterface;
      const filterValue = this.getControllerValue(controllerKey);
      if (!this.hasActiveFilterValue(filterValue)) {
        return;
      }

      // Check if the column to remove is required by this filter
      const isRequired = requiredColumns.some(
        (reqCol) => normalizeFieldKey(reqCol) === normalizedColumnKey
      );

      if (isRequired) {
        affectedFilters.push(filterKey);
      }
    });

    // Special handling for paymentStates
    const paymentStates = this.args.controller.paymentStates;
    if (paymentStates && typeof paymentStates === 'string') {
      const selectedStates = paymentStates.split(',').filter(Boolean);
      if (selectedStates.some((state) => normalizeFieldKey(state) === normalizedColumnKey)) {
        affectedFilters.push('paymentStates');
      }
    }

    return affectedFilters;
  };

  /**
   * Handle column toggle click - checks if warning is needed before toggling
   * Shows modal if column is required by active filters
   */
  handleColumnToggle = (columnKey: string): void => {
    const normalizedNewKey = normalizeFieldKey(columnKey);
    const currentColumns = new Set(this.selectedColumnsArray);

    // Find if there's already a column with the same normalized key
    const existingKey = Array.from(currentColumns).find(
      (key) => normalizeFieldKey(key) === normalizedNewKey
    );

    // Check if we're trying to remove an existing column (it's currently selected)
    if (existingKey) {
      // Check if this column is required by any active filters
      const requiredByFilters = this.getFiltersRequiringColumn(existingKey);

      if (requiredByFilters.length > 0) {
        // Show warning modal instead of toggling immediately
        this.columnToRemove = existingKey;
        this.affectedFilters = requiredByFilters;
        this.showColumnWarningModal = true;
        return; // Don't toggle - wait for user confirmation
      }
    }

    // Safe to toggle immediately
    this.toggleColumn(columnKey);
  };

  toggleColumn = (columnKey: string) => {
    const normalizedNewKey = normalizeFieldKey(columnKey);
    const currentColumns = new Set(this.selectedColumnsArray);

    // Find if there's already a column with the same normalized key
    const existingKey = Array.from(currentColumns).find(
      (key) => normalizeFieldKey(key) === normalizedNewKey
    );

    if (existingKey) {
      // Remove the existing key
      currentColumns.delete(existingKey);
    } else {
      // Add the new key
      currentColumns.add(columnKey);
    }

    this.setSelectedColumns(Array.from(currentColumns));
  };

  /**
   * Confirm removal of column and clear affected filters
   */
  confirmColumnRemoval = () => {
    if (!this.columnToRemove) return;

    const currentColumns = new Set(this.selectedColumnsArray);
    currentColumns.delete(this.columnToRemove);
    this.setSelectedColumns(Array.from(currentColumns));

    // Clear affected filters
    const updates: Record<string, unknown> = {};
    this.affectedFilters.forEach((filterKey) => {
      if (filterKey === 'paymentStates') {
        // Remove the specific payment state
        const states = this.args.controller.paymentStates?.split(',').filter(Boolean) || [];
        const normalizedColumnKey = normalizeFieldKey(this.columnToRemove!);
        const remainingStates = states.filter(
          (state) => normalizeFieldKey(state) !== normalizedColumnKey
        );
        updates.paymentStates = remainingStates.join(',');
      } else {
        // Clear the filter
        updates[filterKey] = null;
      }
    });

    if (Object.keys(updates).length > 0) {
      this.updateQueryParameters(updates);
    }

    this.cancelColumnRemoval();
  };

  /**
   * Cancel column removal
   */
  cancelColumnRemoval = () => {
    this.showColumnWarningModal = false;
    this.columnToRemove = null;
    this.affectedFilters = [];
  };

  selectAllColumns = () => {
    this.setSelectedColumns(this.availableFields.map((f) => f.key));
  };

  deselectAllColumns = () => {
    this.setSelectedColumns([]);
  };

  get groupedFields(): Array<[string, FieldOption[]]> {
    return groupFieldOptions(this.availableFields);
  }

  /***************************/
  /* Report Management      */
  /***************************/

  get reportSearchConfig() {
    // For chart reports, use chart-specific filters and settings
    if (this.currentView === 'chart') {
      const chartConfig = this.chart.toConfig();
      return {
        viewType: this.currentView,
        filters: {
          // Chart queries are based on field selection and time ranges, not table filters
          selectedField: chartConfig.selectedField,
          selectedDataset: chartConfig.selectedDataset,
          selectedTimeRange: chartConfig.selectedTimeRange,
        },
        chartSettings: chartConfig,
      };
    } else {
      // For table reports, use the full filter set
      return {
        viewType: this.currentView,
        filters: {
          programTemplateId: this.args.controller.programTemplateId,
          programCreatedAtStart: this.args.controller.programCreatedAtStart,
          programCreatedAtEnd: this.args.controller.programCreatedAtEnd,
          excludeNonVoucherPrograms: this.args.controller.excludeNonVoucherPrograms,
          courseCashProgramsOnly: this.args.controller.courseCashProgramsOnly,
          applicationStates: this.args.controller.applicationStates?.split(',').filter(Boolean),
          applicationCreatedAtStart: this.args.controller.applicationCreatedAtStart,
          applicationCreatedAtEnd: this.args.controller.applicationCreatedAtEnd,
          paymentStates: this.args.controller.paymentStates?.split(',').filter(Boolean),
          paymentYear: this.args.controller.paymentYear,
          includeCourses: this.args.controller.includeCourses,
          coursesBeginDate: this.args.controller.coursesBeginDate,
          coursesEndDate: this.args.controller.coursesEndDate,
          employeeId: this.args.controller.employeeId,
          terminatedEmployees: this.args.controller.terminatedEmployees,
          excludeTermVerified: this.args.controller.excludeTermVerified,
          approverId: this.args.controller.approverId,
        },
        tableSettings: {
          showIndividualApplications: this.effectiveShowIndividualApplications,
          selectedColumns: this.args.controller.selectedColumns,
          columnOrder: this.columnOrder,
        },
      };
    }
  }

  /** Default table filter values — preserves current page/limit, resets all filters. */
  private get defaultTableFilterState(): Record<string, unknown> {
    return {
      page: this.args.controller.page,
      limit: this.args.controller.limit,
      programTemplateId: undefined,
      programCreatedAtStart: undefined,
      programCreatedAtEnd: undefined,
      excludeNonVoucherPrograms: false,
      showIndividualApplications: false,
      applicationStates: undefined,
      applicationCreatedAtStart: undefined,
      applicationCreatedAtEnd: undefined,
      includePayments: false,
      paymentStates: undefined,
      paymentYear: undefined,
      includeCourses: false,
      coursesBeginDate: undefined,
      coursesEndDate: undefined,
      employeeId: undefined,
      terminatedEmployees: false,
      excludeTermVerified: false,
      approverId: undefined,
      includeInvoiceDate: false,
      courseCashProgramsOnly: false,
    };
  }

  handleLoadReport = (report: ReportModel) => {
    // Set the view based on the report type first
    this.currentView = report.isChartBuilder ? 'chart' : 'table';

    if (report.searchConfig) {
      const config: SearchConfig = report.searchConfig;

      if (this.currentView === 'chart') {
        // Chart values are set by SavedReportsDrawer via chart.setField(), etc.
        // We only need to reset table filters to defaults for chart reports.
        this.args.controller.setProperties(this.defaultTableFilterState);
      } else {
        // For table reports, use the existing table filter loading logic
        // Reset column order to default - will be overridden if saved report has one
        this.columnOrder = [];

        const defaultState = this.defaultTableFilterState;

        const savedValues: Record<string, unknown> = {};

        // Extract saved values from the loaded configuration using object destructuring
        if (config.filters) {
          const filters = config.filters as Record<string, unknown>;

          const programTemplateId = filters.programTemplateId as string | undefined;
          const programCreatedAtStart = filters.programCreatedAtStart as string | undefined;
          const programCreatedAtEnd = filters.programCreatedAtEnd as string | undefined;
          const excludeNonVoucherPrograms = filters.excludeNonVoucherPrograms as
            | boolean
            | undefined;
          const courseCashProgramsOnly = filters.courseCashProgramsOnly as boolean | undefined;
          const applicationStates = filters.applicationStates as string | string[] | undefined;
          const applicationCreatedAtStart = filters.applicationCreatedAtStart as string | undefined;
          const applicationCreatedAtEnd = filters.applicationCreatedAtEnd as string | undefined;
          const paymentStates = filters.paymentStates as string | string[] | undefined;
          const paymentYear = filters.paymentYear as string | undefined;
          const includeCourses = filters.includeCourses as boolean | undefined;
          const coursesBeginDate = filters.coursesBeginDate as string | undefined;
          const coursesEndDate = filters.coursesEndDate as string | undefined;
          const employeeId = filters.employeeId as string | undefined;
          const terminatedEmployees = filters.terminatedEmployees as boolean | undefined;
          const excludeTermVerified = filters.excludeTermVerified as boolean | undefined;
          const approverId = filters.approverId as string | undefined;

          // Build savedValues object with only defined values
          Object.assign(savedValues, {
            ...(programTemplateId !== undefined && { programTemplateId }),
            ...(programCreatedAtStart !== undefined && { programCreatedAtStart }),
            ...(programCreatedAtEnd !== undefined && { programCreatedAtEnd }),
            ...(excludeNonVoucherPrograms !== undefined && { excludeNonVoucherPrograms }),
            ...(courseCashProgramsOnly !== undefined && { courseCashProgramsOnly }),
            ...(applicationStates !== undefined && {
              applicationStates: Array.isArray(applicationStates)
                ? applicationStates.join(',')
                : applicationStates,
            }),
            ...(applicationCreatedAtStart !== undefined && { applicationCreatedAtStart }),
            ...(applicationCreatedAtEnd !== undefined && { applicationCreatedAtEnd }),
            ...(paymentStates !== undefined && {
              paymentStates: Array.isArray(paymentStates) ? paymentStates.join(',') : paymentStates,
            }),
            ...(paymentYear !== undefined && { paymentYear }),
            ...(includeCourses !== undefined && { includeCourses }),
            ...(coursesBeginDate !== undefined && { coursesBeginDate }),
            ...(coursesEndDate !== undefined && { coursesEndDate }),
            ...(employeeId !== undefined && { employeeId }),
            ...(terminatedEmployees !== undefined && { terminatedEmployees }),
            ...(excludeTermVerified !== undefined && { excludeTermVerified }),
            ...(approverId !== undefined && { approverId }),
          });
        }

        // Apply table settings
        if (config.tableSettings?.showIndividualApplications !== undefined) {
          savedValues.showIndividualApplications = config.tableSettings.showIndividualApplications;
        }
        if (config.tableSettings?.selectedColumns) {
          savedValues.selectedColumns = config.tableSettings.selectedColumns;
        }
        if (config.tableSettings?.columnOrder) {
          this.columnOrder = config.tableSettings.columnOrder;
        }

        // Merge default state with saved values (saved values override defaults)
        const finalState = { ...defaultState, ...savedValues };

        // Auto-enable includePayments if paymentStates has values
        if (finalState.paymentStates && String(finalState.paymentStates).trim() !== '') {
          finalState.includePayments = true;
        }

        // Auto-enable includeCourses if course date filters have values
        if (
          (finalState.coursesBeginDate && String(finalState.coursesBeginDate).trim() !== '') ||
          (finalState.coursesEndDate && String(finalState.coursesEndDate).trim() !== '')
        ) {
          finalState.includeCourses = true;
        }

        // Apply the complete state in one operation
        this.args.controller.setProperties(finalState);
      }
    } else {
      // No config - reset to clean state based on view type
      if (this.currentView === 'chart') {
        this.chart.reset();
      }

      // Always reset table filters to default state
      this.args.controller.setProperties(this.defaultTableFilterState);
    }
  };

  handleViewChange = (view: 'table' | 'chart') => {
    this.currentView = view;
    // Clear current report when switching views since this represents a new report type
    this.currentReport = undefined;
  };

  handleDatasetChange = (dataset: string) => {
    this.chart.selectedDataset = dataset;

    // Clear generic filters when switching datasets
    this.genericFilters = {};

    // Reset ALL query params to their controller defaults so they are removed from the URL.
    // Ember only strips a QP from the URL when it matches the default value, so string
    // params that default to `undefined` must be reset to `undefined` (not '').
    // Don't set showIndividualApplications for non-TAS datasets — the
    // effectiveShowIndividualApplications getter already returns true for them,
    // and setting it to true would leak it into the URL (default is false).
    this.args.controller.setProperties({
      dataset,
      page: 1,
      selectedColumns: undefined,
      programTemplateId: undefined,
      programCreatedAtStart: undefined,
      programCreatedAtEnd: undefined,
      excludeNonVoucherPrograms: false,
      showIndividualApplications: false,
      applicationStates: undefined,
      applicationCreatedAtStart: undefined,
      applicationCreatedAtEnd: undefined,
      includePayments: false,
      paymentStates: undefined,
      paymentYear: undefined,
      includeCourses: false,
      coursesBeginDate: undefined,
      coursesEndDate: undefined,
      employeeId: undefined,
      terminatedEmployees: false,
      excludeTermVerified: false,
      approverId: undefined,
      includeInvoiceDate: false,
      courseCashProgramsOnly: false,
      sortField: undefined,
    });
  };

  setCurrentReport = (report: ReportModel) => {
    this.currentReport = report;
  };

  /**
   * Handle column sort from table header clicks.
   * Stores the sort as camelCase field with :asc/:desc suffix.
   * The data provider converts to snake_case at the OpenSearch boundary.
   */
  handleSort = (sortField: string) => {
    this.args.controller.setProperties({
      sortField,
      page: 1,
    });
  };

  get dataProviderComponent() {
    return this.args.dataProviderComponent || ProgramsDataProvider;
  }

  /**
   * Handle column order changes from the table
   * Stores the new order for saving in reports
   */
  handleColumnOrderChange = (newOrder: string[]) => {
    this.columnOrder = newOrder;
    console.log('Column order updated:', newOrder);
  };

  <template>
    <this.dataProviderComponent
      @company={{@company}}
      @selectedDataset={{this.chart.selectedDataset}}
      @programTemplateId={{@controller.programTemplateId}}
      @programCreatedAtStart={{@controller.programCreatedAtStart}}
      @programCreatedAtEnd={{@controller.programCreatedAtEnd}}
      @excludeNonVoucherPrograms={{@controller.excludeNonVoucherPrograms}}
      @courseCashProgramsOnly={{@controller.courseCashProgramsOnly}}
      @showIndividualApplications={{this.effectiveShowIndividualApplications}}
      @applicationStates={{@controller.applicationStates}}
      @applicationCreatedAtStart={{@controller.applicationCreatedAtStart}}
      @applicationCreatedAtEnd={{@controller.applicationCreatedAtEnd}}
      @includePayments={{@controller.includePayments}}
      @paymentStates={{@controller.paymentStates}}
      @paymentYear={{@controller.paymentYear}}
      @includeCourses={{@controller.includeCourses}}
      @coursesBeginDate={{@controller.coursesBeginDate}}
      @coursesEndDate={{@controller.coursesEndDate}}
      @employeeId={{@controller.employeeId}}
      @terminatedEmployees={{@controller.terminatedEmployees}}
      @excludeTermVerified={{@controller.excludeTermVerified}}
      @approverId={{@controller.approverId}}
      @includeInvoiceDate={{@controller.includeInvoiceDate}}
      @selectedColumns={{this.selectedColumnsArray}}
      @genericFilters={{this.genericFilters}}
      @sortField={{@controller.sortField}}
      @page={{@controller.page}}
      @limit={{@controller.limit}}
      @updateQueryParameters={{this.updateQueryParameters}}
      as |provider|
    >
      <StickyLayout
        @sidebarWidth="w-16"
        @sidebarSticky={{true}}
        class={{this.mainContentClasses}}
        {{stickySidebarAdjustment}}
      >
        <:header>
          <Section>
            <:header>
              {{yield to="breadcrumbs"}}
            </:header>
            <:actions>
              <div class="flex items-center gap-2">
                {{yield
                  (hash
                    downloadCsv=provider.downloadCsv
                    currentView=this.currentView
                    onViewChange=this.handleViewChange
                    selectedDataset=this.chart.selectedDataset
                    onDatasetChange=this.handleDatasetChange
                  )
                  to="headerActions"
                }}
              </div>
            </:actions>
          </Section>
        </:header>

        <:main>
          {{#if (eq this.currentView "table")}}
            {{#if provider.isLoading}}
              <div class="flex items-center justify-center p-8">
                <div class="text-gray-500 dark:text-gray-400">Loading table data...</div>
              </div>
            {{else}}
              <Section class="overflow-x-auto m-6">
                <:body>
                  {{#if this.effectiveShowIndividualApplications}}
                    <ProgramsTable
                      @results={{provider.data}}
                      @isLoading={{provider.isLoading}}
                      @selectedColumns={{this.selectedColumnsArray}}
                      @availableFields={{this.availableFields}}
                      @columnOrder={{this.columnOrder}}
                      @showIndividualApplications={{this.effectiveShowIndividualApplications}}
                      @excludeNonVoucherPrograms={{@controller.excludeNonVoucherPrograms}}
                      @courseCashProgramsOnly={{@controller.courseCashProgramsOnly}}
                      @includeCourses={{@controller.includeCourses}}
                      @includeInvoiceDate={{@controller.includeInvoiceDate}}
                      @terminatedEmployees={{@controller.terminatedEmployees}}
                      @excludeTermVerified={{@controller.excludeTermVerified}}
                      @applicationDetailRoute={{@applicationDetailRoute}}
                      @programDetailRoute={{@programDetailRoute}}
                      @paymentStates={{@controller.paymentStates}}
                      @sortField={{@controller.sortField}}
                      @onSort={{this.handleSort}}
                      @limit={{@controller.limit}}
                      @page={{@controller.page}}
                      @selectedFilters={{this.selectedFilters}}
                      @onPageChange={{fn (mut @controller.page)}}
                      @onColumnOrderChange={{this.handleColumnOrderChange}}
                    />
                  {{else}}
                    <ProgramsAggregateTable
                      @results={{provider.data}}
                      @isLoading={{provider.isLoading}}
                      @limit={{@controller.limit}}
                      @page={{@controller.page}}
                      @selectedColumns={{this.selectedColumnsArray}}
                      @availableFields={{this.availableFields}}
                      @selectedFilters={{this.selectedFilters}}
                      @onPageChange={{fn (mut @controller.page)}}
                    />
                  {{/if}}
                </:body>
              </Section>
            {{/if}}
          {{else}}
            {{! Chart view }}
            <Section class="m-6">
              <:header>
                📊 Chart View -
                {{provider.totalRecords}}
                records ({{provider.executionTime}}ms)
              </:header>
              <:body>
                {{#if provider.isLoading}}
                  <div class="flex items-center justify-center p-8">
                    <div class="text-gray-500 dark:text-gray-400">Loading chart data...</div>
                  </div>
                {{else}}
                  <ChartView
                    @selectedField={{this.chart.selectedField}}
                    @chartType={{this.chart.selectedType}}
                    @dateGrouping={{this.chart.dateGrouping}}
                    @showDataTable={{this.chart.showDataTable}}
                    @selectedDataset={{this.chart.selectedDataset}}
                    @selectedTimeRange={{this.chart.selectedTimeRange}}
                    @summaryTitle={{this.chart.summaryTitle}}
                    @sortOrder={{this.chart.sortOrder}}
                    @onFieldChange={{this.chart.setField}}
                    @onChartTypeChange={{this.chart.setType}}
                    @onDateGroupingChange={{this.chart.setDateGrouping}}
                    @onShowDataTableChange={{this.chart.setShowDataTable}}
                    @onDatasetChange={{this.chart.setDataset}}
                    @onTimeRangeChange={{this.chart.setTimeRange}}
                    @onSummaryTitleChange={{this.chart.setSummaryTitle}}
                    @onSortOrderChange={{this.chart.setSortOrder}}
                  />
                {{/if}}
              </:body>
            </Section>
          {{/if}}
        </:main>

        <:sidebar>
          <TooltipButton
            @tooltip="Advanced Filters"
            @tooltipPlacement="left"
            @appearance="minimal"
            @intent="default"
            @size="sm"
            class="!p-1 !rounded-md"
            @tooltipDisabled={{this.isDrawerOpen}}
            data-tour="advanced-filters-button"
            {{on "click" (fn this.openDrawer "advanced-filters")}}
          >
            <span class="relative inline-flex">
              <PhFunnel @size={{24}} class="text-gray-600 dark:text-gray-300" />
              {{#if this.hasActiveAdvancedFilters}}
                <span
                  class="absolute w-3 h-3 bg-red-500 rounded-full border-2 border-white dark:border-gray-900 -top-1 -right-1"
                  aria-label="Active filters"
                ></span>
              {{/if}}
            </span>
          </TooltipButton>

          {{#if @showSaveReports}}
            <TooltipButton
              @tooltip="Load & Save Report"
              @tooltipPlacement="left"
              @appearance="minimal"
              @intent="default"
              @size="sm"
              @icon={{PhFloppyDisk}}
              @iconSize={{24}}
              class="!p-1 !rounded-md"
              @tooltipDisabled={{this.isDrawerOpen}}
              {{on "click" (fn this.openDrawer "load-save")}}
            />
          {{/if}}

          <TooltipButton
            @tooltip="Download CSV"
            @tooltipPlacement="left"
            @appearance="minimal"
            @intent="default"
            @size="sm"
            @icon={{PhTrayArrowDown}}
            @iconSize={{24}}
            class="!p-1 !rounded-md"
            @tooltipDisabled={{this.isDrawerOpen}}
            {{on "click" provider.downloadCsv}}
          />
        </:sidebar>
      </StickyLayout>

      {{! Drawer for Advanced Filters and Load/Save }}
      <div class="drawer-container {{if this.isDrawerOpen 'drawer-open' 'drawer-closed'}}">
        <Drawer
          @isOpen={{this.isDrawerOpen}}
          @onClose={{this.closeDrawer}}
          @placement="right"
          @size="lg"
          @closeOnOutsideClick={{this.drawerCloseOnOutsideClick}}
        >
          <:header>
            {{#if (eq this.selectedDrawerType "advanced-filters")}}
              Advanced Options
            {{else if (eq this.selectedDrawerType "load-save")}}
              Load & Save Report
            {{/if}}
          </:header>
          <:body>
            {{#if (eq this.selectedDrawerType "advanced-filters")}}
              {{#if @showColumnSelector}}
                <div class="border-b border-gray-200 dark:border-gray-700 mb-4">
                  <nav class="-mb-px flex space-x-8 px-4" aria-label="Tabs">
                    <button
                      type="button"
                      class="{{if
                          (eq this.activeFilterTab 'filters')
                          'border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                          'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-500'
                        }}
                        whitespace-nowrap py-3 px-2 border-b-2 font-medium text-sm"
                      {{on "click" this.selectFiltersTab}}
                    >
                      Filters
                    </button>
                    <button
                      type="button"
                      class="{{if
                          (eq this.activeFilterTab 'columns')
                          'border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                          'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-500'
                        }}
                        whitespace-nowrap py-3 px-2 border-b-2 font-medium text-sm"
                      {{on "click" this.selectColumnsTab}}
                    >
                      Columns
                    </button>
                  </nav>
                </div>
              {{/if}}

              {{#if (eq this.activeFilterTab "filters")}}
                <div class="{{if @showColumnSelector 'p-4' 'pt-0 p-4'}}">
                  {{#if this.isTasDataset}}
                    <AdvancedProgramFilters
                      @company={{@company}}
                      @isAdminContext={{@isAdminContext}}
                      @onCompanyChange={{@onCompanyChange}}
                      @availableFieldKeys={{this.availableFieldKeys}}
                      @programTemplateId={{@controller.programTemplateId}}
                      @programCreatedAtStart={{@controller.programCreatedAtStart}}
                      @programCreatedAtEnd={{@controller.programCreatedAtEnd}}
                      @excludeNonVoucherPrograms={{@controller.excludeNonVoucherPrograms}}
                      @courseCashProgramsOnly={{@controller.courseCashProgramsOnly}}
                      @showIndividualApplications={{this.effectiveShowIndividualApplications}}
                      @applicationStates={{@controller.applicationStates}}
                      @applicationCreatedAtStart={{@controller.applicationCreatedAtStart}}
                      @applicationCreatedAtEnd={{@controller.applicationCreatedAtEnd}}
                      @applicationDateRangePreset={{@controller.applicationDateRangePreset}}
                      @includePayments={{@controller.includePayments}}
                      @paymentStates={{@controller.paymentStates}}
                      @paymentYear={{@controller.paymentYear}}
                      @includeCourses={{@controller.includeCourses}}
                      @coursesBeginDate={{@controller.coursesBeginDate}}
                      @coursesEndDate={{@controller.coursesEndDate}}
                      @employeeId={{@controller.employeeId}}
                      @terminatedEmployees={{@controller.terminatedEmployees}}
                      @excludeTermVerified={{@controller.excludeTermVerified}}
                      @approverId={{@controller.approverId}}
                      @includeInvoiceDate={{@controller.includeInvoiceDate}}
                      @onFilterUpdate={{this.updateQueryParameters}}
                    />
                  {{else}}
                    <div class="flex flex-col gap-y-6">
                      {{#if this.shouldShowNonTasPartnerFilter}}
                        <fieldset>
                          <div class="flex flex-col gap-y-3">
                            <h3
                              class="text-xs font-semibold uppercase tracking-wide text-gray-500"
                            >Partner</h3>
                            <ReSelect
                              @label="Filter by Partner"
                              @items={{this.partnerCompanyItems}}
                              @selected={{@company.id}}
                              @onChange={{this.partnerCompanyDidChange}}
                              @searchable={{true}}
                              @clearable={{true}}
                              @placeholder="All Partners"
                              @size="sm"
                              @isLoading={{this.partnerCompaniesData.isLoading}}
                            />
                          </div>
                        </fieldset>
                      {{/if}}

                      <DatasetFilters
                        @fields={{this.availableFields}}
                        @filters={{this.genericFilters}}
                        @onFilterUpdate={{this.updateGenericFilters}}
                        @onClearFilters={{this.clearGenericFilters}}
                        @isLoading={{this.mappingData.isLoading}}
                      />
                    </div>
                  {{/if}}
                </div>
              {{else if (and @showColumnSelector (eq this.activeFilterTab "columns"))}}
                <div class="p-4">
                  <ColumnSelector
                    @groupedFields={{this.groupedFields}}
                    @isColumnSelected={{this.isColumnSelected}}
                    @handleColumnToggle={{this.handleColumnToggle}}
                    @selectAllColumns={{this.selectAllColumns}}
                    @deselectAllColumns={{this.deselectAllColumns}}
                    @isLoading={{this.mappingData.isLoading}}
                    @totalFieldCount={{this.availableFields.length}}
                    @selectedCount={{this.selectedColumnsArray.length}}
                  />
                </div>
              {{/if}}
            {{else if (eq this.selectedDrawerType "load-save")}}
              <SavedReportsDrawer
                @onLoad={{this.handleLoadReport}}
                @onExport={{provider.downloadCsv}}
                @searchData={{this.reportSearchConfig}}
                @reportType={{if (eq this.currentView "chart") "chart_builder" "table_builder"}}
                @currentReport={{this.currentReport}}
                @showPartnerVisibility={{@isGlobalContext}}
                @updateQueryParameters={{this.updateQueryParameters}}
                @setCurrentReport={{this.setCurrentReport}}
                @setDataset={{this.chart.setDataset}}
                @setGroupByField={{this.chart.setField}}
                @setChartType={{this.chart.setType}}
                @setTimeRange={{this.chart.setTimeRange}}
                @setDateGrouping={{this.chart.setDateGrouping}}
                @setShowTableView={{this.chart.setShowDataTable}}
              />
            {{/if}}
          </:body>
        </Drawer>
      </div>

      {{! Column Removal Warning Modal }}
      <Modal
        @isOpen={{this.showColumnWarningModal}}
        @onClose={{this.cancelColumnRemoval}}
        @size="sm"
      >
        <:header>Cannot Remove Column</:header>
        <:body>
          <p class="text-sm text-gray-700 dark:text-gray-300 mb-4">
            This column is required by the following active filter{{if
              (gt this.affectedFilters.length 1)
              "s"
            }}:
          </p>
          <ul class="list-disc list-inside space-y-1 mb-4">
            {{#each this.affectedFilters as |filterKey|}}
              <li class="text-sm font-medium text-gray-900 dark:text-gray-100">
                {{filterKey}}
              </li>
            {{/each}}
          </ul>
          <p class="text-sm text-gray-700 dark:text-gray-300">
            Removing this column will also clear the associated filter{{if
              (gt this.affectedFilters.length 1)
              "s"
            }}. Do you want to proceed?
          </p>
        </:body>
        <:footer>
          <Footer
            @submitText="Remove Column"
            @onSubmit={{this.confirmColumnRemoval}}
            @onCancel={{this.cancelColumnRemoval}}
            @cancelText="Cancel"
            @submitIntent="danger"
          />
        </:footer>
      </Modal>
    </this.dataProviderComponent>
  </template>
}
